import { isDesignMode } from "../../../config/designMode";
import { API_BASE_URL } from "../../../config/env";
import { resolveDesignMock } from "../../../design/resolveDesignMock";
import { useUserStore } from "../../../store/userStore";

export type ChatStreamEvent =
    | { type: "status"; message: string }
    | { type: "token"; content: string }
    | { type: "done"; data: Record<string, unknown> }
    | { type: "error"; message: string; partial?: boolean };

export interface ChatStreamCallbacks {
    onStatus?: (message: string) => void;
    onToken?: (content: string) => void;
    onDone?: (data: Record<string, unknown>) => void;
    onError?: (message: string, partial: boolean) => void;
}

export type ChatStreamHandlers = ChatStreamCallbacks;

function parseSseChunk(buffer: string): { events: ChatStreamEvent[]; rest: string } {
    const events: ChatStreamEvent[] = [];
    const parts = buffer.split("\n\n");
    const rest = parts.pop() ?? "";

    for (const part of parts) {
        const line = part
            .split("\n")
            .find((l) => l.startsWith("data:"));
        if (!line) continue;
        const jsonStr = line.slice(5).trim();
        if (!jsonStr) continue;
        try {
            events.push(JSON.parse(jsonStr) as ChatStreamEvent);
        } catch {
            // ignore malformed frames
        }
    }

    return { events, rest };
}

function handleStreamEvents(
    events: ChatStreamEvent[],
    handlers: ChatStreamCallbacks,
    doneRef: { data: Record<string, unknown> | null }
): void {
    for (const event of events) {
        switch (event.type) {
            case "status":
                handlers.onStatus?.(event.message);
                break;
            case "token":
                handlers.onToken?.(event.content);
                break;
            case "done":
                doneRef.data = event.data;
                handlers.onDone?.(event.data);
                break;
            case "error":
                handlers.onError?.(event.message, Boolean(event.partial));
                throw new Error(event.message);
        }
    }
}

function drainSseBuffer(
    buffer: string,
    handlers: ChatStreamCallbacks,
    doneRef: { data: Record<string, unknown> | null },
    flushTail = false
): string {
    const input = flushTail && buffer.trim() ? `${buffer}\n\n` : buffer;
    const parsed = parseSseChunk(input);
    handleStreamEvents(parsed.events, handlers, doneRef);
    return flushTail ? "" : parsed.rest;
}

const DESIGN_STREAM_CHUNK_SIZE = 24;
const DESIGN_STREAM_CHUNK_DELAY_MS = 20;

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function consumeDesignChatSSE(
    endpoint: string,
    options: RequestInit,
    handlers: ChatStreamCallbacks
): Promise<Record<string, unknown>> {
    const data = await resolveDesignMock(endpoint, options);
    const record =
        data && typeof data === "object" && !Array.isArray(data)
            ? (data as Record<string, unknown>)
            : {};

    handlers.onStatus?.("Gerando resposta (modo design)...");
    await delay(150);

    const text = String(record.natural_language_response ?? "");
    for (let i = 0; i < text.length; i += DESIGN_STREAM_CHUNK_SIZE) {
        handlers.onToken?.(text.slice(i, i + DESIGN_STREAM_CHUNK_SIZE));
        await delay(DESIGN_STREAM_CHUNK_DELAY_MS);
    }

    handlers.onDone?.(record);
    return record;
}

export async function consumeChatSSE(
    endpoint: string,
    options: RequestInit = {},
    handlers: ChatStreamCallbacks = {}
): Promise<Record<string, unknown>> {
    if (isDesignMode) {
        return consumeDesignChatSSE(endpoint, options, handlers);
    }

    const token = useUserStore.getState().tokens.backendToken;
    const method = options.method ?? "GET";
    const headers: HeadersInit = {
        Accept: "text/event-stream",
        ...(method !== "GET" ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Erro ${response.status}: ${text}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error("Resposta sem corpo para streaming.");
    }

    const decoder = new TextDecoder();
    let buffer = "";
    const doneRef: { data: Record<string, unknown> | null } = { data: null };

    while (true) {
        const { value, done } = await reader.read();

        if (value) {
            buffer += decoder.decode(value, { stream: true });
            buffer = drainSseBuffer(buffer, handlers, doneRef);
        }

        if (done) {
            buffer += decoder.decode();
            drainSseBuffer(buffer, handlers, doneRef, true);
            break;
        }
    }

    if (!doneRef.data) {
        throw new Error("Stream encerrado sem resposta final.");
    }

    return doneRef.data;
}
